import { slippageTriggers } from '../data';

const severityBarColor = (severity: string) => {
  if (severity === 'Critical') return 'bg-accent-500';
  if (severity === 'High') return 'bg-primary-500';
  return 'bg-secondary-500';
};

const severityTextColor = (severity: string) => {
  if (severity === 'Critical') return 'text-accent-600';
  if (severity === 'High') return 'text-primary-600';
  return 'text-secondary-600';
};

const severityBgColor = (severity: string) => {
  if (severity === 'Critical') return 'bg-accent-100 text-accent-700';
  if (severity === 'High') return 'bg-primary-100 text-primary-700';
  return 'bg-secondary-100 text-secondary-700';
};

const severityFlex = (severity: string) => {
  if (severity === 'Critical') return 'flex-[36]';
  if (severity === 'High') return 'flex-[26]';
  return 'flex-[12]';
};

export default function SlippageTriggersSection() {
  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        <div className="max-w-[640px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            When slippage strikes hardest
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Slippage is not random — it follows predictable patterns. Know the four biggest triggers and you can plan around them.
          </p>
        </div>

        {/* Severity Spectrum */}
        <div className="mb-10 md:mb-14">
          <div className="text-[11px] font-semibold text-foreground-500 uppercase tracking-[0.15em] mb-4">
            Severity Spectrum
          </div>
          <div className="flex h-3 rounded-full overflow-hidden gap-[3px] mb-5">
            {slippageTriggers.map((t) => (
              <div
                key={t.trigger}
                className={`h-full rounded-full ${severityBarColor(t.severity)} ${severityFlex(t.severity)} transition-all duration-700`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-5 md:gap-x-10 gap-y-3">
            {slippageTriggers.map((t) => (
              <div key={t.trigger} className="flex items-center gap-2.5">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${severityBarColor(t.severity)}`} />
                <span className="text-[14px] font-semibold text-foreground-800">{t.trigger}</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${severityBgColor(t.severity)}`}>
                  {t.severity}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Clean list - no cards, no icon boxes */}
        <div className="divide-y divide-background-200">
          {slippageTriggers.map((trigger, idx) => (
            <div
              key={trigger.trigger}
              className="group flex gap-5 md:gap-8 py-8 md:py-9 first:pt-0 last:pb-0"
            >
              {/* Number + left accent */}
              <div className="flex-shrink-0 flex flex-col items-center gap-2.5 pt-0.5">
                <span className="text-[26px] font-[750] text-foreground-200 leading-none select-none">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span className={`w-1 flex-1 min-h-[40px] rounded-full ${severityBarColor(trigger.severity)}`} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h3 className="text-[18px] font-bold text-foreground-950">
                    {trigger.trigger}
                  </h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${severityBgColor(trigger.severity)}`}>
                    {trigger.severity}
                  </span>
                </div>

                <p className="text-[15px] leading-[1.7] text-foreground-600 mb-4 max-w-[680px]">
                  {trigger.desc}
                </p>

                <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-semibold ${severityTextColor(trigger.severity)} bg-background-50`}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-current" />
                  {trigger.stats}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}